import dayjs from "dayjs";
import lodash from "lodash";
import type { LoaderFunctionArgs, MetaFunction } from "react-router";
import { useLoaderData } from "react-router";
import * as cookies from "~/cookies";
import { buildPlans } from "~/design/plans";
import Landing from "~/design/landing/Landing";
import type { LandingData } from "~/design/landing/types";
import i18nServer from "~/i18n.server";
import { getListResources, getLookupResource } from "~/services/api";
import to from "~/utils/to";

// Public landing page. Items are readable by guests; the currency lookup needs a login on the current API,
// so logged-out visitors see plans with savings but without absolute prices.
export async function loader({ request }: LoaderFunctionArgs) {
  const cookie = await cookies.auth.find(request);
  const loggedIn = !lodash.isEmpty(cookie?.token);
  const token = loggedIn ? cookie.token : undefined;
  const t = await i18nServer.getFixedT(request);

  const receiptsReq = loggedIn
    ? to(getListResources({ token, resource: "receipts", query: { _user: cookie.user._id, status: "available" } }))
    : Promise.resolve([null, null]);
  // The same item query as /subscribe (consumables once a receipt is active, most expensive first), so buildPlans picks the
  // item, and so the price, that checkout preselects for ?plan=.
  const itemsReq = receiptsReq.then(([, r]) =>
    to(getListResources({ token, resource: "items", query: { source: "web", type: lodash.isEmpty(r?.data?.results) ? "subscription" : "consumable", sort: { price: -1 } } })),
  );
  const [[, items], [, currency], [, receipts]] = await Promise.all([
    itemsReq,
    to(getLookupResource({ token, resource: "currencies", query: { code: cookie?.user?.settings?.currency || "IRT" } })),
    receiptsReq,
  ]);

  const receipt = receipts?.data?.results?.[0];
  const endAt = receipt?.period?.endAt;
  const data: LandingData = {
    plans: buildPlans(items?.data?.results, currency?.data?.code ? currency.data : null),
    currency: currency?.data?.code ? currency.data : null,
    loggedIn,
    proDaysLeft: endAt ? Math.max(0, dayjs(endAt).diff(dayjs(), "day")) : null,
    strings: { title: t("title"), description: t("description") },
  };
  return data;
}

export const meta: MetaFunction<typeof loader> = ({ data, matches }) => [
  { title: data?.strings.title },
  { name: "description", content: data?.strings.description },
  // a leaf meta replaces its parents', so keep root's og:* and twitter:* tags: shared links still get a preview card
  ...((matches.find((m) => m.id === "root")?.meta ?? []) as Record<string, string>[]).filter((m) => "property" in m),
];

export default function Index() {
  return <Landing data={useLoaderData<typeof loader>()} />;
}
